/**
 * Trigger a browser download of a remote URL as a file.
 *
 * `window.open(url, '_blank')` or anchor with `download` attribute don't work
 * reliably for cross-origin URLs (Supabase Storage is a different origin than
 * the app). They fall back to navigating-away, which breaks inside sandboxed
 * previews (Claude Preview, Stackblitz, etc.) and gives a worse UX everywhere.
 *
 * This fetches the bytes as a blob and dispatches a synthetic anchor click
 * against an object URL, which works in all environments. Returns a promise so
 * callers can show a toast on failure.
 */
export async function downloadBlob(url: string, filename?: string): Promise<void> {
  // Always materialize remote bytes behind a same-document blob URL. A later
  // Supabase-only optimization navigated directly to `?download=...`; Chrome
  // blocks that cross-origin attachment navigation in sandboxed/embedded app
  // contexts — the exact failure this utility was originally created to avoid.
  // Public Storage responses expose `Access-Control-Allow-Origin: *`, so the
  // regular CORS path is both supported and consistent for images and videos.
  const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
  if (!response.ok) {
    throw new Error(`Download failed: HTTP ${response.status}`);
  }
  const responseBlob = await response.blob();
  const sourceExtension = extensionFromUrl(url);
  const requestedFilename = filename || inferFilename(url);
  const requestedExtension = extensionFromFilename(requestedFilename);
  const resolvedExtension =
    requestedExtension || sourceExtension || extensionFromMime(responseBlob.type);
  const finalFilename = reconcileImageExtension(
    requestedExtension || !resolvedExtension
      ? requestedFilename
      : `${requestedFilename}.${resolvedExtension}`,
    requestedExtension,
    responseBlob.type,
  );
  const inferredMime = mimeFromExtension(requestedExtension || sourceExtension);
  const blob =
    isGenericMime(responseBlob.type) && inferredMime
      ? new Blob([responseBlob], { type: inferredMime })
      : responseBlob;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = finalFilename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after the click lands — some browsers need the URL live briefly.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
}

const MIME_BY_EXTENSION: Readonly<Record<string, string>> = Object.freeze({
  avif: 'image/avif',
  csv: 'text/csv',
  gif: 'image/gif',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  json: 'application/json',
  m4a: 'audio/mp4',
  m4v: 'video/mp4',
  md: 'text/markdown',
  mov: 'video/quicktime',
  mp3: 'audio/mpeg',
  mp4: 'video/mp4',
  pdf: 'application/pdf',
  png: 'image/png',
  svg: 'image/svg+xml',
  txt: 'text/plain',
  wav: 'audio/wav',
  webm: 'video/webm',
  webp: 'image/webp',
  zip: 'application/zip',
});

function reconcileImageExtension(filename: string, requested: string, blobMime: string): string {
  if (!requested || !blobMime.toLowerCase().startsWith('image/')) return filename;
  const requestedMime = mimeFromExtension(requested);
  if (!requestedMime?.startsWith('image/')) return filename;
  const actual = extensionFromMime(blobMime);
  if (!actual || mimeFromExtension(actual) === requestedMime) return filename;
  return `${filename.slice(0, filename.length - requested.length)}${actual}`;
}

function extensionFromFilename(filename: string): string {
  const match = /\.([a-z0-9]+)$/i.exec(filename.trim());
  return match?.[1]?.toLowerCase() ?? '';
}

function extensionFromUrl(url: string): string {
  try {
    return extensionFromFilename(new URL(url).pathname);
  } catch {
    return extensionFromFilename(url.split(/[?#]/, 1)[0] ?? '');
  }
}

function mimeFromExtension(extension: string): string | undefined {
  return MIME_BY_EXTENSION[extension];
}

function extensionFromMime(mime: string): string {
  const normalized = mime.split(';', 1)[0]?.trim().toLowerCase() ?? '';
  return (
    Object.entries(MIME_BY_EXTENSION).find(
      ([extension, candidate]) => candidate === normalized && extension !== 'jpeg',
    )?.[0] ?? ''
  );
}

function isGenericMime(mime: string): boolean {
  const normalized = mime.split(';', 1)[0]?.trim().toLowerCase() ?? '';
  return normalized === '' || normalized === 'application/octet-stream';
}

function inferFilename(url: string): string {
  try {
    const u = new URL(url);
    const last = u.pathname.split('/').filter(Boolean).pop() ?? 'download';
    return decodeURIComponent(last);
  } catch {
    return 'download';
  }
}
